(() => {
  const money = (cents) => {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.Galactica?.moneyFormat);
    }
    const value = (Number(cents) / 100).toFixed(2).replace('.', ',');
    return `${value} €`;
  };

  const parseGrams = (label) => {
    if (!label) return null;
    const match = String(label)
      .toLowerCase()
      .replace(',', '.')
      .match(/(\d+(?:\.\d+)?)\s*g\b/);
    if (match) return Number(match[1]);
    const compact = String(label).toLowerCase().match(/(\d+(?:\.\d+)?)g(?:ramme?s?)?$/);
    return compact ? Number(compact[1]) : null;
  };

  const minPricePerGram = (product, { fromPrefix = true } = {}) => {
    let best = null;
    let weightCount = 0;
    for (const v of product.variants || []) {
      const label = v.public_title || v.title || v.option1 || '';
      const g = parseGrams(label);
      if (!g || g <= 0) continue;
      if (!(Number(v.price) > 0)) continue;
      weightCount += 1;
      const ppg = v.price / g;
      if (best === null || ppg < best.ppg) best = { ppg, price: v.price, g };
    }
    if (!best) return null;
    const formatted = `${money(Math.round(best.ppg))}/g`;
    if (fromPrefix && weightCount > 1) return `À partir de ${formatted}`;
    return formatted;
  };

  const extractCbdLabel = (product) => {
    const fromTags = (product.tags || []).find((t) => /cbd/i.test(t) && /%/.test(t));
    if (fromTags) return fromTags;
    const titleMatch = String(product.title || '').match(/\d+(?:[.,]\d+)?\s*%\s*cbd/i);
    if (titleMatch) return titleMatch[0];
    return null;
  };

  const readWishlist = () => {
    try {
      const raw = JSON.parse(localStorage.getItem('gal-wishlist') || '[]');
      return raw.filter((x) => typeof x === 'string' && x.length > 0);
    } catch {
      return [];
    }
  };

  const syncWishlistPressed = () => {
    const list = readWishlist();
    document.querySelectorAll('[data-gal-wishlist]').forEach((btn) => {
      const handle = btn.dataset.productHandle;
      if (!handle) return;
      btn.setAttribute('aria-pressed', list.includes(handle) ? 'true' : 'false');
    });
  };

  class GalProductCard extends HTMLElement {
    connectedCallback() {
      this.bindVariantUi();
    }

    bindVariantUi() {
      this.select = this.querySelector('[data-gal-variant-select]');
      this.pads = [...this.querySelectorAll('[data-gal-variant-pad]')];
      this.variantInput = this.querySelector('[data-gal-variant-id]');
      this.addBtn = this.querySelector('[data-gal-add]');
      this.priceRoot = this.querySelector('[data-gal-price]');
      this.valueLabel = this.querySelector('[data-gal-card-value-label]');
      this.form = this.querySelector('form');

      /* Host-level delegation survives innerHTML swaps / section re-renders */
      if (this.dataset.galCardBound !== '1') {
        this.dataset.galCardBound = '1';
        this.addEventListener('change', (event) => {
          const select = event.target?.closest?.('[data-gal-variant-select]');
          if (!select || !this.contains(select)) return;
          this.select = select;
          const option = select.selectedOptions[0];
          this.syncPads(option?.value);
          this.applyVariant(option, { userSelected: true });
        });
        this.addEventListener('click', (event) => {
          const pad = event.target?.closest?.('[data-gal-variant-pad]');
          if (!pad || !this.contains(pad) || pad.disabled) return;
          this.pads = [...this.querySelectorAll('[data-gal-variant-pad]')];
          this.select = this.querySelector('[data-gal-variant-select]');
          this.syncPads(pad.dataset.variantId);
          if (this.select) this.select.value = pad.dataset.variantId;
          this.applyVariant(pad, { userSelected: true });
        });
        this.addEventListener('submit', async (event) => {
          if (event.target !== this.querySelector('form')) return;
          if (!window.Galactica || window.Galactica.settings.cartType !== 'drawer') return;
          event.preventDefault();
          this.variantInput = this.querySelector('[data-gal-variant-id]');
          const id = this.variantInput?.value;
          if (!id) return;
          try {
            await fetch(window.Galactica.routes.cart_add_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
            });
            document.dispatchEvent(new CustomEvent('gal:cart:refresh'));
            document.dispatchEvent(new CustomEvent('gal:cart:open'));
          } catch (error) {
            event.target.submit();
          }
        });
      }

      /* Prefer an available option so closed <select> never looks blank */
      if (this.select) {
        const selected = this.select.selectedOptions[0];
        const selectedUnavailable =
          selected &&
          (selected.disabled || String(selected.dataset.available) === 'false');
        if (selectedUnavailable) {
          const available = [...this.select.options].find(
            (o) => !o.disabled && String(o.dataset.available) !== 'false',
          );
          if (available) this.select.value = available.value;
        }
      }

      const initial =
        this.select?.selectedOptions?.[0] ||
        this.pads.find((p) => p.classList.contains('is-active')) ||
        this.pads[0];
      if (initial) this.applyVariant(initial, { userSelected: false });
    }

    syncPads(variantId) {
      const id = String(variantId || '');
      this.pads.forEach((p) => {
        const active = String(p.dataset.variantId) === id;
        p.classList.toggle('is-active', active);
        p.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    applyVariant(source, { userSelected = false } = {}) {
      if (!source) return;
      const id = source.dataset.variantId || source.value;
      const price = Number(source.dataset.price || 0);
      const compare = Number(source.dataset.compare || 0);
      const available = String(source.dataset.available) !== 'false' && source.dataset.available !== 'false';
      const label = source.dataset.label || source.textContent || '';
      const valueLabel = source.dataset.valueLabel || '';
      const grams =
        Number(source.dataset.grams) ||
        parseGrams(label) ||
        Number(this.priceRoot?.dataset.grams) ||
        null;
      if (this.priceRoot && grams) this.priceRoot.dataset.grams = String(grams);

      this.syncPads(id);
      if (this.select && id) this.select.value = id;
      if (this.variantInput) this.variantInput.value = id;
      if (this.addBtn) {
        this.addBtn.disabled = !available;
        this.addBtn.textContent = available
          ? window.Galactica?.strings?.addToCart || this.addBtn.textContent || 'Ajouter'
          : window.Galactica?.strings?.soldOut || 'Épuisé';
      }
      if (this.valueLabel) {
        if (valueLabel) {
          this.valueLabel.hidden = false;
          this.valueLabel.textContent = valueLabel;
        } else {
          this.valueLabel.hidden = true;
          this.valueLabel.textContent = '';
        }
      }
      if (this.priceRoot) {
        const current = this.priceRoot.querySelector('[data-gal-price-current]');
        const compareEl = this.priceRoot.querySelector('[data-gal-price-compare]');
        let perGramEl = this.priceRoot.querySelector('[data-gal-price-per-gram]');
        if (current) current.textContent = money(price);
        if (compare > price) {
          if (!compareEl) {
            const span = document.createElement('span');
            span.className = 'gal-price__compare';
            span.dataset.galPriceCompare = '';
            current?.after(span);
          }
          const el = this.priceRoot.querySelector('[data-gal-price-compare]');
          if (el) el.textContent = money(compare);
        } else if (compareEl) {
          compareEl.remove();
        }
        if (grams && grams > 0 && perGramEl) {
          perGramEl.textContent = `${money(Math.round(price / grams))}/g`;
        }
      }

      const fromEl = this.querySelector('[data-gal-card-per-gram]');
      if (fromEl && grams && grams > 0) {
        const multi = fromEl.dataset.galMultiWeight === '1';
        const selectedPpg = money(Math.round(price / grams));
        if (userSelected || !multi) {
          fromEl.textContent = `${selectedPpg}/g`;
          fromEl.dataset.galSelected = '1';
        } else if (!fromEl.dataset.galSelected) {
          const fromLabel = fromEl.dataset.galFromLabel || 'À partir de';
          const bestCents = Number(fromEl.dataset.galBestPerGram || 0);
          fromEl.textContent = bestCents
            ? `${fromLabel} ${money(bestCents)}/g`
            : `${fromLabel} ${selectedPpg}/g`;
        }
      }

      this.dispatchEvent(
        new CustomEvent('gal:variant-change', { bubbles: true, detail: { id, price, available, grams } })
      );
    }
  }

  class GalCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-gal-cart-panel]');
      this.closeButtons = this.querySelectorAll('[data-gal-cart-close]');
      this.closeButtons.forEach((btn) => btn.addEventListener('click', () => this.close()));
      this.querySelector('[data-gal-cart-overlay]')?.addEventListener('click', () => this.close());
      document.addEventListener('gal:cart:open', () => this.open());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.querySelector('[data-gal-cart-close]')?.focus();
    }

    close() {
      this.classList.remove('is-open');
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }

  class GalPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-gal-search-results]');
      if (!this.input || !this.results) return;
      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.query(this.input.value.trim()), 220);
      });
      this.input.addEventListener('focus', () => {
        if (this.results.innerHTML.trim()) this.results.hidden = false;
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.results.hidden = true;
      });
    }

    async query(terms) {
      if (!terms) {
        this.results.innerHTML = '';
        this.results.hidden = true;
        return;
      }
      const url = `${window.Galactica.routes.predictive_search_url}?q=${encodeURIComponent(terms)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
      try {
        const res = await fetch(url);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const content = doc.querySelector('#predictive-search-results') || doc.body;
        this.results.innerHTML = content.innerHTML;
        this.results.hidden = false;
      } catch (e) {
        this.results.innerHTML = '';
        this.results.hidden = true;
      }
    }
  }

  class GalQuiz extends HTMLElement {
    connectedCallback() {
      this.step = 0;
      this.answers = {};
      this.loadingTimer = null;
      this.steps = [...this.querySelectorAll('[data-gal-quiz-step]')];
      try {
        this.catalog = JSON.parse(this.querySelector('[data-gal-quiz-catalog]')?.textContent || '[]');
      } catch {
        this.catalog = [];
      }
      this.render();
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-gal-quiz-restart]')) {
          this.reset();
          return;
        }
        const back = e.target.closest('[data-gal-quiz-back]');
        const answer = e.target.closest('[data-gal-quiz-answer]');
        if (answer) {
          this.answers[answer.dataset.key] = answer.dataset.value;
          this.step = Math.min(this.step + 1, this.steps.length - 1);
          this.render();
          if (this.steps[this.step]?.hasAttribute('data-gal-quiz-loading-step')) {
            this.runAnalysis();
          }
        }
        if (back) {
          clearTimeout(this.loadingTimer);
          this.step = Math.max(this.step - 1, 0);
          this.render();
        }
      });
    }

    disconnectedCallback() {
      clearTimeout(this.loadingTimer);
    }

    reset() {
      clearTimeout(this.loadingTimer);
      this.step = 0;
      this.answers = {};
      this.render();
    }

    runAnalysis() {
      clearTimeout(this.loadingTimer);
      const needleHost = this.querySelector('[data-gal-finder-needle]');
      const play = window.Galactica?.playNeedleMeter;
      const finish = () => {
        this.step = Math.min(this.step + 1, this.steps.length - 1);
        this.render();
        if (this.steps[this.step]?.hasAttribute('data-gal-quiz-results-step')) this.showResults();
      };
      if (typeof play === 'function' && needleHost) {
        play(needleHost, { duration: 1100 }).then(finish);
      } else {
        this.loadingTimer = setTimeout(finish, 900);
      }
    }

    parseCbd(value) {
      if (value == null || value === '') return null;
      const n = Number(String(value).replace('%', '').replace(',', '.').trim());
      return Number.isFinite(n) ? n : null;
    }

    bestVariant(product) {
      const variants = (product.variants || []).filter((v) => v && v.available !== false);
      const pool = variants.length ? variants : product.variants || [];
      if (!pool.length) return null;
      const format = (this.answers.format || '').toLowerCase();
      const withGrams = pool
        .map((v) => ({
          ...v,
          grams: v.grams || parseGrams(v.title) || null,
          ppg: v.grams || parseGrams(v.title) ? v.price / (v.grams || parseGrams(v.title)) : null,
        }))
        .filter((v) => v.grams);

      if (format === 'best_pg' && withGrams.length) {
        return withGrams.sort((a, b) => a.ppg - b.ppg)[0];
      }
      if (format === 'petit' && withGrams.length) {
        return withGrams.sort((a, b) => a.grams - b.grams)[0];
      }
      if (format === 'grand' && withGrams.length) {
        return withGrams.sort((a, b) => b.grams - a.grams)[0];
      }
      if (format === 'standard' && withGrams.length) {
        const mid = withGrams.find((v) => v.grams >= 5 && v.grams <= 15) || withGrams.sort((a, b) => a.grams - b.grams)[Math.floor(withGrams.length / 2)];
        return mid;
      }
      return pool.find((v) => v.available) || pool[0];
    }

    familyLabel(key) {
      return (
        {
          fleurs: 'Fleur',
          resines: 'Résine',
          rocks: 'Rock / concentré',
          any: 'Produit',
        }[key] || 'Produit'
      );
    }

    scoreProduct(product) {
      let score = 0;
      const facts = [];
      const blob = `${product.title} ${product.type} ${(product.tags || []).join(' ')} ${(product.collections || []).join(' ')}`.toLowerCase();
      const family = (this.answers.family || '').toLowerCase();
      const familyMap = {
        fleurs: ['fleur', 'fleurs', 'bud', 'indoor', 'greenhouse', 'herbe', 'fleurs-indoor', 'fleurs-greenhouse'],
        resines: ['résine', 'resine', 'pollen', 'hash', 'charas', 'resines', 'resines-et-pollen'],
        rocks: ['rock', 'moonrock', 'goldrock', 'icerock', 'concentré', 'concentre', 'moonrock'],
      };

      if (family && family !== 'any') {
        const keys = familyMap[family] || [];
        const hit =
          keys.some((k) => blob.includes(k)) ||
          (product.collections || []).some((h) => keys.some((k) => String(h).includes(k)));
        if (hit) {
          score += 8;
          facts.push(this.familyLabel(family));
        } else {
          score -= 2;
        }
      } else {
        score += 1;
      }

      const profile = (this.answers.profile || '').toLowerCase();
      const cbd = this.parseCbd(product.cbd);
      const intensity = String(product.intensity || '').toLowerCase();
      if (profile === 'doux') {
        if ((cbd != null && cbd < 18) || /doux|soft|léger|leger/.test(intensity + blob)) {
          score += 5;
          facts.push('profil doux');
        } else if (cbd != null && cbd < 25) score += 2;
      } else if (profile === 'equilibre') {
        if ((cbd != null && cbd >= 18 && cbd <= 28) || /équilibr|medium|moyen/.test(intensity + blob)) {
          score += 5;
          facts.push('profil équilibré');
        } else if (cbd != null) score += 2;
      } else if (profile === 'intense') {
        if ((cbd != null && cbd >= 25) || /fort|intense|strong|puissant|ultra/.test(intensity + blob)) {
          score += 5;
          facts.push('profil intense');
        } else if (cbd != null && cbd >= 20) score += 2;
      } else if (profile === 'decouvrir') {
        score += 2;
        facts.push('sélection découverte');
      }

      const variant = this.bestVariant(product);
      const grams = variant?.grams || parseGrams(variant?.title);
      const format = (this.answers.format || '').toLowerCase();
      if (variant) {
        if (format === 'petit' && grams && grams <= 5) {
          score += 4;
          facts.push(`format ${variant.title}`);
        } else if (format === 'standard' && grams && grams >= 5 && grams <= 25) {
          score += 4;
          facts.push(`format ${variant.title}`);
        } else if (format === 'grand' && grams && grams >= 25) {
          score += 4;
          facts.push(`format ${variant.title}`);
        } else if (format === 'best_pg' && grams) {
          score += 4;
          facts.push('bon prix au gramme');
        } else if (grams) {
          score += 1;
          facts.push(`format ${variant.title} disponible`);
        }
      }

      if (product.available || (variant && variant.available)) score += 3;
      else score -= 6;

      if (product.growing) facts.push(String(product.growing));
      if (cbd != null) score += 1;

      const reason = facts.filter(Boolean).slice(0, 3).join(' · ') || 'Disponible · correspond à tes choix';
      return { score, reason, variant, cbd };
    }

    showResults() {
      const root = this.querySelector('[data-gal-quiz-results]');
      if (!root) return;
      const ranked = this.catalog
        .map((p) => ({ ...p, ...this.scoreProduct(p) }))
        .filter((p) => p.score > 0 && (p.available || p.variant?.available))
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);

      if (!ranked.length) {
        root.innerHTML =
          '<p class="gal-finder__empty">Aucune correspondance nette pour l’instant. Parcours le catalogue ou recommence.</p>';
        return;
      }

      root.innerHTML = ranked
        .map((p, index) => {
          const v = p.variant || {};
          const price = money(v.price || 0);
          const grams = v.grams || parseGrams(v.title);
          const perGram = grams ? `${money(Math.round(v.price / grams))}/g` : '';
          const cbd = p.cbd != null ? `${p.cbd}% CBD` : '';
          const second =
            p.cbc && String(p.cbc).trim()
              ? `${String(p.cbc).replace('%', '')}% CBC`
              : p.cbg && String(p.cbg).trim()
                ? `${String(p.cbg).replace('%', '')}% CBG`
                : '';
          const stars =
            p.rating && Number(p.rating) > 0
              ? `<p class="gal-finder__result-reviews"><span aria-hidden="true">${'★'.repeat(Math.round(Number(p.rating)))}${'☆'.repeat(5 - Math.round(Number(p.rating)))}</span>${p.ratingCount ? ` ${p.ratingCount} avis` : ''}</p>`
              : '';
          return `<article class="gal-finder__result${index === 0 ? ' is-primary' : ''}">
            ${index === 0 ? '<p class="gal-finder__badge">Meilleur match</p>' : ''}
            <div class="gal-finder__result-grid">
              ${p.image ? `<a class="gal-finder__result-media" href="${p.url}"><img src="${p.image}" alt="" width="240" height="240" loading="lazy"></a>` : '<div class="gal-finder__result-media"></div>'}
              <div class="gal-finder__result-body">
                <h4><a href="${p.url}">${p.title}</a></h4>
                ${stars}
                <p class="gal-finder__result-meta">${[v.title, perGram, cbd, second].filter(Boolean).join(' · ')}</p>
                <p class="gal-finder__result-price">${price}</p>
                <p class="gal-finder__result-why">${p.reason}</p>
                <div class="gal-finder__result-actions">
                  <a class="gal-btn gal-btn--primary" href="${p.url}">Voir le produit</a>
                </div>
              </div>
            </div>
          </article>`;
        })
        .join('');
    }

    render() {
      this.steps.forEach((step, index) => {
        step.hidden = index !== this.step;
      });
      const dialog = this.closest('[data-gal-finder]');
      const meters = dialog?.querySelectorAll('[data-gal-finder-step-meter]');
      const questionSteps = this.steps.filter(
        (s) => !s.hasAttribute('data-gal-quiz-results-step') && !s.hasAttribute('data-gal-quiz-loading-step')
      ).length;
      if (meters?.length) {
        const activeIdx = Math.min(this.step, questionSteps - 1);
        const inQuestions = this.step < questionSteps;
        meters.forEach((meter, i) => {
          const on = inQuestions && i <= activeIdx;
          meter.classList.toggle('is-active', on);
          meter.style.setProperty('--gal-vu', on ? '100%' : '0%');
        });
      }
      const label = dialog?.querySelector('[data-gal-finder-step-label]');
      if (label) {
        if (this.steps[this.step]?.hasAttribute('data-gal-quiz-loading-step')) label.textContent = 'Analyse en cours';
        else if (this.steps[this.step]?.hasAttribute('data-gal-quiz-results-step')) label.textContent = 'Résultats';
        else label.textContent = `Étape ${Math.min(this.step + 1, questionSteps)} sur ${questionSteps}`;
      }
      const firstFocus = this.steps[this.step]?.querySelector('button, a, [tabindex]:not([tabindex="-1"])');
      if (firstFocus && dialog?.open) {
        requestAnimationFrame(() => firstFocus.focus({ preventScroll: true }));
      }
    }
  }

  class GalOrderTracker extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.form?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const order = this.form.querySelector('[name="order"]')?.value?.trim();
        const email = this.form.querySelector('[name="email"]')?.value?.trim();
        const out = this.querySelector('[data-gal-tracker-output]');
        const requiredMsg = this.dataset.msgRequired || 'Order number and email are required.';
        const pendingMsg =
          this.dataset.msgPending ||
          'Online tracking is not available yet. Check your confirmation email or contact support.';
        if (!order || !email) {
          if (out) out.textContent = requiredMsg;
          return;
        }
        const endpoint = (this.dataset.trackingEndpoint || '').trim();
        if (!endpoint) {
          if (out) out.textContent = pendingMsg;
          return;
        }
        if (out) out.textContent = '…';
        try {
          const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ order, email }),
          });
          const data = await res.json().catch(() => null);
          if (!res.ok || !data) {
            if (out) out.textContent = pendingMsg;
            return;
          }
          const lines = [];
          if (data.carrier) lines.push(String(data.carrier));
          if (data.status) lines.push(String(data.status));
          if (data.tracking_url) lines.push(String(data.tracking_url));
          if (out) out.textContent = lines.length ? lines.join(' · ') : pendingMsg;
        } catch {
          if (out) out.textContent = pendingMsg;
        }
      });
    }
  }

  const COMPARE_KEY = 'gal-compare';
  const COMPARE_MAX = 3;
  const COMPARE_LABEL = 'Comparer';
  const COMPARE_ADDED = 'Ajouté';

  const readCompareList = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(COMPARE_KEY) || '[]');
      if (!Array.isArray(raw)) return [];
      return raw
        .map((item) => (typeof item === 'string' ? { handle: item } : item))
        .filter((item) => item && typeof item.handle === 'string' && item.handle.length)
        .slice(0, COMPARE_MAX);
    } catch {
      return [];
    }
  };

  const writeCompareList = (list, { silent = false } = {}) => {
    localStorage.setItem(COMPARE_KEY, JSON.stringify(list.slice(0, COMPARE_MAX)));
    if (!silent) {
      document.dispatchEvent(new CustomEvent('gal:compare:update', { detail: { list: readCompareList() } }));
    }
  };

  const moleculeFromProduct = (product, key) => {
    const re = new RegExp(`${key}\\s*[:=]?\\s*(\\d+(?:[.,]\\d+)?)\\s*%`, 'i');
    for (const tag of product.tags || []) {
      const m = String(tag).match(re);
      if (m) return `${m[1].replace(',', '.')}%`;
    }
    const title = String(product.title || '');
    const m2 = title.match(re);
    if (m2) return `${m2[1].replace(',', '.')}%`;
    return null;
  };

  const vuCell = (label, pct) => {
    if (!pct) return '—';
    const match = String(pct).match(/(\d+(?:[.,]\d+)?)/);
    if (!match) return '—';
    const num = Math.min(100, Math.max(0, parseFloat(match[1].replace(',', '.')) || 0));
    if (num <= 0) return '—';
    const segs = 12;
    const on = Math.max(1, Math.min(segs, Math.round((num / 100) * segs)));
    const bars = Array.from({ length: segs }, (_, i) => `<span class="gal-vu__seg"${i < on ? ' data-lit="1"' : ''}></span>`).join('');
    const channel = (side) =>
      `<div class="gal-vu__channel" data-gal-vu-ch="${side}"><span class="gal-vu__ch-label">${side}</span><div class="gal-vu__meter"><div class="gal-vu__segments">${bars}</div></div></div>`;
    return `<div class="gal-vu gal-vu--inline gal-vu--stereo" style="--gal-vu:${num}%" data-gal-vu="${label.toLowerCase()}" data-gal-vu-pct="${num}" data-gal-vu-on="${on}"><div class="gal-vu__head"><span class="gal-vu__label">${label}</span><span class="gal-vu__value">${num}%</span></div><div class="gal-vu__stereo" aria-hidden="true">${channel('L')}${channel('R')}</div></div>`;
  };

  class GalCompare extends HTMLElement {
    connectedCallback() {
      this.dock = document.querySelector('[data-gal-compare-dock]');
      this.modal = document.querySelector('[data-gal-compare-modal]');
      this.board = this.modal?.querySelector('[data-gal-compare-board]') || document.querySelector('[data-gal-compare-board]');
      this.status = this.modal?.querySelector('[data-gal-compare-status]');
      this.loading = this.modal?.querySelector('[data-gal-compare-loading]');
      this.thumbs = document.querySelector('[data-gal-compare-thumbs]');
      this.bound = false;

      document.addEventListener('click', (e) => this.onClick(e));
      document.addEventListener('gal:compare:update', () => this.syncUi());
      this.modal?.addEventListener('click', (e) => {
        if (e.target === this.modal) this.closeModal();
      });
      this.syncUi();
    }

    onClick(e) {
      const openBtn = e.target.closest('[data-gal-compare-open]');
      if (openBtn) {
        e.preventDefault();
        this.openModal();
        return;
      }
      const closeBtn = e.target.closest('[data-gal-compare-close]');
      if (closeBtn) {
        e.preventDefault();
        this.closeModal();
        return;
      }
      const clearBtn = e.target.closest('[data-gal-compare-clear]');
      if (clearBtn) {
        e.preventDefault();
        writeCompareList([]);
        if (this.modal?.open) this.renderBoard();
        return;
      }
      const removeBtn = e.target.closest('[data-gal-compare-remove]');
      if (removeBtn) {
        e.preventDefault();
        const handle = removeBtn.dataset.productHandle;
        writeCompareList(readCompareList().filter((x) => x.handle !== handle));
        if (this.modal?.open) this.renderBoard();
        return;
      }
      const btn = e.target.closest('[data-gal-compare]');
      if (!btn) return;
      e.preventDefault();
      const handle = btn.dataset.productHandle;
      if (!handle) return;
      let list = readCompareList();
      const exists = list.some((x) => x.handle === handle);
      if (exists) {
        list = list.filter((x) => x.handle !== handle);
      } else {
        if (list.length >= COMPARE_MAX) {
          this.openModal();
          if (this.status) {
            this.status.textContent = `Maximum ${COMPARE_MAX} produits. Retire-en un pour en ajouter un autre.`;
          }
          return;
        }
        list.push({
          handle,
          title: btn.dataset.productTitle || handle,
          image: btn.dataset.productImage || '',
          cbd: btn.dataset.productCbd || '',
          cbg: btn.dataset.productCbg || '',
          cbc: btn.dataset.productCbc || '',
          type: btn.dataset.productType || '',
        });
      }
      writeCompareList(list);
      if (!exists && list.length === 1) {
        /* tray appears via syncUi */
      }
    }

    syncUi() {
      const list = readCompareList();
      const count = list.length;
      document.querySelectorAll('[data-gal-compare-count]').forEach((el) => {
        el.textContent = String(count);
      });
      document.querySelectorAll('[data-gal-compare]').forEach((btn) => {
        const on = list.some((x) => x.handle === btn.dataset.productHandle);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        const label = btn.querySelector('[data-gal-compare-label]');
        if (label) label.textContent = on ? COMPARE_ADDED : COMPARE_LABEL;
        else if (!btn.querySelector('img') && btn.childNodes.length <= 1) btn.textContent = on ? COMPARE_ADDED : COMPARE_LABEL;
      });
      if (this.dock) {
        this.dock.hidden = count < 1;
        this.dock.classList.toggle('is-visible', count >= 1);
      }
      if (this.thumbs) {
        this.thumbs.innerHTML = list
          .map((item) => {
            const img = item.image
              ? `<img src="${item.image}" alt="" width="40" height="40" loading="lazy">`
              : `<span class="gal-compare-dock__thumb-fallback">${(item.title || '?').slice(0, 1)}</span>`;
            return `<button type="button" class="gal-compare-dock__thumb" data-gal-compare-remove data-product-handle="${item.handle}" title="Retirer">${img}</button>`;
          })
          .join('');
      }
    }

    openModal() {
      if (!this.modal) {
        window.location.href = '/pages/compare';
        return;
      }
      if (typeof this.modal.showModal === 'function') this.modal.showModal();
      else this.modal.setAttribute('open', '');
      document.body.classList.add('gal-compare-open');
      this.renderBoard();
    }

    closeModal() {
      if (!this.modal) return;
      if (typeof this.modal.close === 'function') this.modal.close();
      else this.modal.removeAttribute('open');
      document.body.classList.remove('gal-compare-open');
    }

    async renderBoard() {
      if (!this.board) return;
      const list = readCompareList();
      if (this.loading) this.loading.hidden = false;
      this.board.innerHTML = '';
      if (this.status) {
        if (list.length < 2) {
          this.status.textContent =
            list.length === 0
              ? 'Ajoute 2 ou 3 produits via le bouton Comparer.'
              : 'Ajoute encore 1 produit pour lancer la comparaison.';
        } else {
          this.status.textContent = `${list.length} produits sélectionnés.`;
        }
      }

      if (!list.length) {
        if (this.loading) this.loading.hidden = true;
        this.board.innerHTML = `<div class="gal-compare-empty"><p>Aucun produit pour l’instant.</p><button type="button" class="gal-btn gal-btn--secondary" data-gal-compare-close>Continuer mes achats</button></div>`;
        return;
      }

      const needleHost = this.loading?.querySelector('[data-gal-compare-needle]') || this.loading;
      const play = window.Galactica?.playNeedleMeter;
      const needlePromise =
        typeof play === 'function' && this.loading && !this.loading.hidden
          ? play(needleHost, { duration: 900 })
          : Promise.resolve();

      const products = [];
      for (const item of list) {
        try {
          const res = await fetch(`/products/${item.handle}.js`);
          if (!res.ok) continue;
          const p = await res.json();
          products.push({ product: p, meta: item });
          const idx = list.findIndex((x) => x.handle === item.handle);
          if (idx >= 0) {
            list[idx] = {
              ...item,
              handle: p.handle,
              title: p.title,
              image: p.featured_image || item.image || '',
              type: p.type || item.type || '',
            };
          }
        } catch (err) {
          /* skip */
        }
      }
      writeCompareList(
        list.map((x) => ({
          handle: x.handle,
          title: x.title,
          image: x.image,
          cbd: x.cbd || '',
          cbg: x.cbg || '',
          cbc: x.cbc || '',
          type: x.type || '',
        })),
        { silent: true }
      );
      this.syncUi();
      await needlePromise;
      if (this.loading) this.loading.hidden = true;

      if (products.length < 2) {
        const only = products[0]?.product;
        this.board.innerHTML = `<div class="gal-compare-empty">
          ${
            only
              ? `<article class="gal-compare-card gal-compare-card--solo">
                  ${only.featured_image ? `<img src="${only.featured_image}" alt="" width="160" height="160" loading="lazy">` : ''}
                  <h3>${only.title}</h3>
                  <p class="gal-price">${money(only.price)}</p>
                  <button type="button" class="gal-btn gal-btn--text" data-gal-compare-remove data-product-handle="${only.handle}">Retirer</button>
                </article>`
              : ''
          }
          <p class="gal-muted">Ajoute encore 1 produit pour lancer la comparaison.</p>
        </div>`;
        return;
      }

      const rows = [
        {
          label: 'Image',
          values: products.map(({ product: p }) =>
            p.featured_image
              ? `<a href="/products/${p.handle}"><img src="${p.featured_image}" alt="" width="160" height="160" loading="lazy"></a>`
              : '—'
          ),
          hideIfEmpty: false,
        },
        {
          label: 'Produit',
          values: products.map(({ product: p }) => `<strong><a href="/products/${p.handle}">${p.title}</a></strong>`),
        },
        {
          label: 'Type',
          values: products.map(({ product: p, meta }) => p.type || meta.type || ''),
        },
        {
          label: 'Prix',
          values: products.map(({ product: p }) => {
            const v = (p.variants || []).find((x) => x.available) || p.variants?.[0];
            const price = v?.price ?? p.price;
            const compare = v?.compare_at_price || 0;
            return compare > price
              ? `<span class="gal-price">${money(price)}</span> <s class="gal-price__compare">${money(compare)}</s>`
              : `<span class="gal-price">${money(price)}</span>`;
          }),
        },
        { label: '€/g', values: products.map(({ product: p }) => minPricePerGram(p) || '') },
        {
          label: 'Formats',
          values: products.map(({ product: p }) =>
            (p.variants || [])
              .map((v) => v.public_title || v.title)
              .filter(Boolean)
              .join(', ')
          ),
        },
        {
          label: 'CBD',
          values: products.map(
            ({ product: p, meta }) =>
              vuCell('CBD', meta.cbd || moleculeFromProduct(p, 'cbd') || extractCbdLabel(p))
          ),
        },
        {
          label: 'CBG',
          values: products.map(({ product: p, meta }) => vuCell('CBG', meta.cbg || moleculeFromProduct(p, 'cbg'))),
        },
        {
          label: 'CBC',
          values: products.map(({ product: p, meta }) => vuCell('CBC', meta.cbc || moleculeFromProduct(p, 'cbc'))),
        },
        {
          label: 'Disponibilité',
          values: products.map(({ product: p }) => (p.available ? 'En stock' : 'Épuisé')),
        },
        {
          label: '',
          values: products.map(
            ({ product: p }) =>
              `<div class="gal-compare-card__actions"><a class="gal-btn gal-btn--primary" href="/products/${p.handle}">Voir</a><button type="button" class="gal-btn gal-btn--text" data-gal-compare-remove data-product-handle="${p.handle}">Retirer</button></div>`
          ),
          hideIfEmpty: false,
        },
      ].filter((row) => {
        if (row.hideIfEmpty === false) return true;
        return row.values.some((v) => v && v !== '—' && String(v).trim() !== '');
      });

      this.board.innerHTML = `<div class="gal-compare-board" style="--gal-compare-cols:${products.length}">
        <div class="gal-compare-board__head">
          <div class="gal-compare-board__label"></div>
          ${products
            .map(
              ({ product: p }) =>
                `<div class="gal-compare-board__col-head"><button type="button" class="gal-btn gal-btn--text" data-gal-compare-remove data-product-handle="${p.handle}">Retirer</button></div>`
            )
            .join('')}
        </div>
        ${rows
          .map(
            (row) => `<div class="gal-compare-board__row">
              <div class="gal-compare-board__label">${row.label}</div>
              ${row.values.map((v) => `<div class="gal-compare-board__cell">${v || '—'}</div>`).join('')}
            </div>`
          )
          .join('')}
      </div>`;
    }
  }

  class GalCompareBoard extends HTMLElement {
    connectedCallback() {
      this.board = this.querySelector('[data-gal-compare-board]');
      const host = document.querySelector('gal-compare');
      document.addEventListener('gal:compare:update', () => {
        if (host && typeof host.renderBoard === 'function' && this.board && !document.querySelector('[data-gal-compare-modal][open], [data-gal-compare-modal].open')) {
          /* page fallback: reuse host renderer by temporarily pointing board */
        }
      });
      const syncPage = async () => {
        if (!this.board || !host) return;
        const prev = host.board;
        host.board = this.board;
        host.status = this.querySelector('[data-gal-compare-status]') || host.status;
        await host.renderBoard();
        host.board = prev;
      };
      document.addEventListener('gal:compare:update', () => syncPage());
      syncPage();
      this.querySelector('[data-gal-compare-clear]')?.addEventListener('click', () => writeCompareList([]));
    }
  }

  class GalWishlistBoard extends HTMLElement {
    connectedCallback() {
      this.board = this.querySelector('[data-gal-wishlist-board]');
      this.empty = this.querySelector('[data-gal-wishlist-empty]');
      this.render();
      document.addEventListener('gal:wishlist:update', () => this.render());
    }

    read() {
      return readWishlist();
    }

    async render() {
      const handles = this.read();
      if (!handles.length) {
        if (this.board) this.board.innerHTML = '';
        if (this.empty) this.empty.hidden = false;
        return;
      }
      if (this.empty) this.empty.hidden = true;
      if (!this.board) return;
      this.board.innerHTML = '<p class="gal-muted">Chargement…</p>';
      const cards = [];
      for (const handle of handles) {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) continue;
          const p = await res.json();
          const v = (p.variants || []).find((x) => x.available) || p.variants?.[0];
          const perGram = minPricePerGram(p);
          cards.push(`<article class="gal-box gal-box--bordered" style="padding:1rem">
            ${p.featured_image ? `<a href="/products/${p.handle}"><img src="${p.featured_image}" alt="" width="400" height="400" loading="lazy" style="width:100%;aspect-ratio:1;object-fit:cover"></a>` : ''}
            <h3><a href="/products/${p.handle}">${p.title}</a></h3>
            <p class="gal-price">${money(p.price)}${perGram ? ` <span class="gal-price__per-gram">${perGram}</span>` : ''}</p>
            <div style="display:flex;flex-wrap:wrap;gap:.5rem;margin-top:.75rem">
              <a class="gal-btn gal-btn--primary" href="/products/${p.handle}">Voir</a>
              ${v ? `<button type="button" class="gal-btn gal-btn--action" data-gal-wishlist-quick-add data-variant-id="${v.id}">Ajouter</button>` : ''}
              <button type="button" class="gal-btn gal-btn--text" data-gal-wishlist data-product-handle="${p.handle}" data-product-id="${p.id}">Retirer</button>
            </div>
          </article>`);
        } catch (e) {
          /* skip */
        }
      }
      this.board.innerHTML = cards.length ? cards.join('') : '<p class="gal-muted">Aucun produit valide dans ta liste.</p>';
      this.board.querySelectorAll('[data-gal-wishlist-quick-add]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.variantId;
          if (!id) return;
          try {
            await fetch(window.Galactica.routes.cart_add_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
            });
            document.dispatchEvent(new CustomEvent('gal:cart:refresh'));
            document.dispatchEvent(new CustomEvent('gal:cart:open'));
          } catch (err) {
            /* ignore */
          }
        });
      });
    }
  }

  class GalCarousel extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-gal-carousel-track]');
      this.querySelector('[data-gal-carousel-prev]')?.addEventListener('click', () => {
        track?.scrollBy({ left: -320, behavior: 'smooth' });
      });
      this.querySelector('[data-gal-carousel-next]')?.addEventListener('click', () => {
        track?.scrollBy({ left: 320, behavior: 'smooth' });
      });
    }
  }

  class GalQuickView extends HTMLElement {
    connectedCallback() {
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-gal-quick-view]');
        if (!btn) return;
        const handle = btn.dataset.productHandle;
        if (!handle) return;
        this.innerHTML = `<div class="gal-drawer is-open" role="dialog" aria-modal="true"><div class="gal-drawer__overlay" data-close></div><div class="gal-drawer__panel"><button type="button" class="gal-btn gal-btn--text" data-close>Fermer</button><p>Chargement…</p></div></div>`;
        const close = () => {
          this.innerHTML = '';
        };
        this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));

        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) throw new Error('fetch failed');
          const p = await res.json();
          const v = (p.variants || []).find((x) => x.available) || p.variants?.[0];
          const label = v?.public_title || v?.title || v?.option1 || '';
          const grams = parseGrams(label);
          const perGram = grams && v ? `${money(Math.round(v.price / grams))}/g` : minPricePerGram(p);
          const panel = this.querySelector('.gal-drawer__panel');
          if (panel) {
            panel.innerHTML = `
              <button type="button" class="gal-btn gal-btn--text" data-close>Fermer</button>
              ${p.featured_image ? `<img src="${p.featured_image}" alt="" width="600" height="600" loading="lazy" style="width:100%;max-height:320px;object-fit:cover">` : ''}
              <h2 style="margin-top:1rem">${p.title}</h2>
              <p class="gal-price">${money(v?.price || p.price)}${perGram ? ` <span class="gal-price__per-gram">${perGram}</span>` : ''}</p>
              <div style="display:flex;flex-wrap:wrap;gap:.5rem;margin-top:1rem">
                <a class="gal-btn gal-btn--primary" href="/products/${p.handle}">Voir la fiche</a>
                ${
                  v
                    ? `<button type="button" class="gal-btn gal-btn--action" data-gal-qv-add data-variant-id="${v.id}" ${v.available ? '' : 'disabled'}>Ajouter</button>`
                    : ''
                }
              </div>`;
            panel.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));
            panel.querySelector('[data-gal-qv-add]')?.addEventListener('click', async () => {
              const id = v?.id;
              if (!id) return;
              try {
                await fetch(window.Galactica.routes.cart_add_url, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
                });
                document.dispatchEvent(new CustomEvent('gal:cart:refresh'));
                document.dispatchEvent(new CustomEvent('gal:cart:open'));
                close();
              } catch (err) {
                window.location.href = `/products/${p.handle}`;
              }
            });
          }
        } catch (err) {
          const panel = this.querySelector('.gal-drawer__panel');
          if (panel) {
            panel.innerHTML = `<button type="button" class="gal-btn gal-btn--text" data-close>Fermer</button><p>Impossible de charger le produit.</p><a class="gal-btn gal-btn--primary" href="/products/${handle}">Voir la fiche</a>`;
            panel.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));
          }
        }
      });
    }
  }

  class GalVariantSelector extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', () => {
        this.dispatchEvent(new CustomEvent('gal:variant-change', { bubbles: true }));
      });
    }
  }

  class GalRelatedProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.galRelatedReady === '1') return;
      this.dataset.galRelatedReady = '1';
      const url = this.dataset.url;
      if (!url) return;
      this.fetchRelated(url);
    }

    async fetchRelated(url) {
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = doc.querySelector('gal-related-products') || doc.querySelector('.gal-related');
        const grid = incoming?.querySelector('[data-gal-related-grid]');
        const local = this.querySelector('[data-gal-related-grid]');
        if (!grid || !local || !grid.innerHTML.trim()) return;
        local.innerHTML = grid.innerHTML;
      } catch (_) {
        /* keep fallback */
      }
    }
  }

  class GalHomeShowcase extends HTMLElement {
    connectedCallback() {
      if (this.dataset.galHsReady === '1') return;
      this.dataset.galHsReady = '1';
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.querySelectorAll('.gal-home-showcase__track').forEach((track) => {
          track.style.animation = 'none';
          track.style.transform = 'none';
        });
      }
    }
  }

  const registry = {
    'gal-product-card': GalProductCard,
    'gal-cart-drawer': GalCartDrawer,
    'gal-predictive-search': GalPredictiveSearch,
    'gal-quiz': GalQuiz,
    'gal-order-tracker': GalOrderTracker,
    'gal-compare': GalCompare,
    'gal-compare-board': GalCompareBoard,
    'gal-wishlist-board': GalWishlistBoard,
    'gal-carousel': GalCarousel,
    'gal-quick-view': GalQuickView,
    'gal-variant-selector': GalVariantSelector,
    'gal-related-products': GalRelatedProducts,
    'gal-home-showcase': GalHomeShowcase,
  };

  Object.entries(registry).forEach(([name, ctor]) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  });

  document.addEventListener('click', (e) => {
    const wish = e.target.closest('[data-gal-wishlist]');
    if (!wish) return;
    const handle = wish.dataset.productHandle;
    if (!handle) return;
    const key = 'gal-wishlist';
    let list = readWishlist();
    if (list.includes(handle)) list = list.filter((x) => x !== handle);
    else list.push(handle);
    localStorage.setItem(key, JSON.stringify(list));
    wish.setAttribute('aria-pressed', list.includes(handle) ? 'true' : 'false');
    document.dispatchEvent(new CustomEvent('gal:wishlist:update', { detail: { list } }));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncWishlistPressed);
  } else {
    syncWishlistPressed();
  }
})();
